const PROGRESS_TYPES = new Set([
  "status",
  "thinking",
  "executing",
  "tool_queued",
  "tool_start",
  "tool_complete",
  "error",
  "escalation",
]);

const RESTART_HEAD_CHARS = 40;
/** Normalized opening of a text block; null when too short to mean anything. */
function restartHead(text) {
  const head = String(text || "").replace(/[#*_`>\s]+/g, " ").trim().toLowerCase().slice(0, RESTART_HEAD_CHARS);
  return head.length === RESTART_HEAD_CHARS ? head : null;
}

function updateStreamingMessage(message, event, now, options = {}) {
  if (
    !message &&
    ["tool_queued", "tool_start", "tool_complete"].includes(event.type)
  ) {
    return message;
  }
  const blocks = [...(message?.contentBlocks || [])];

  if (event.type === "tool_queued" && event.tool) {
    blocks.push({
      type: "tool",
      name: event.tool,
      toolId: event.toolId,
      status: "queued",
      input: {},
      result: null,
      expanded: false,
    });
  } else if (event.type === "tool_start" && event.tool) {
    const index = blocks.findIndex(
      (block) =>
        block.type === "tool" &&
        block.toolId === event.toolId &&
        block.status === "queued",
    );
    const runningTool = {
      type: "tool",
      name: event.tool,
      toolId: event.toolId,
      status: "running",
      input: event.input || {},
      result: null,
      expanded: false,
    };
    if (index >= 0) blocks[index] = { ...blocks[index], ...runningTool };
    else blocks.push(runningTool);
  } else if (event.type === "tool_complete" && event.tool) {
    return message
      ? {
          ...message,
          contentBlocks: blocks.map((block) =>
            block.type === "tool" && block.toolId === event.toolId
              ? {
                  ...block,
                  status: event.success !== false ? "success" : "failed",
                  result: event.result || event.content,
                }
              : block,
          ),
        }
      : message;
  } else if (event.type === "text_delta") {
    const last = blocks[blocks.length - 1];
    if (last?.type === "text") {
      blocks[blocks.length - 1] = {
        ...last,
        content: last.content + (event.content || ""),
      };
    } else {
      blocks.push({ type: "text", content: event.content || "" });
    }
  } else if (event.type === "text") {
    blocks.push({ type: "text", content: event.content || "" });
  } else if (event.type === "text_reset") {
    // DRAFT WITHDRAWN (2026-09-21): a server guard bounced the draft that just
    // streamed and is regenerating the answer. No tool ran in a bounced pass,
    // so the draft is exactly the trailing text run — drop it NOW, before a
    // tool card can land after it and make it look like mid-loop narration.
    if (!message) return message;
    let keep = blocks.length;
    while (keep > 0 && blocks[keep - 1].type === "text") keep -= 1;
    return { ...message, contentBlocks: blocks.slice(0, keep), isStreaming: true };
  } else if (event.type === "text_end") {
    if (!message) return null;
    // STREAM CONTAINMENT (stream_containment flag, 2026-09-04): text_end
    // carries the authoritative post-guard final text from the server. The
    // streamed text_delta draft reached the screen BEFORE any guard ran — if
    // zero_tool_claim_guard regenerated the response, the fabricated draft
    // would otherwise stand uncorrected. Replace only the TRAILING streamed
    // text run (the final answer) with the authoritative content; mid-loop
    // narration text between tool cards and the tool cards themselves are
    // untouched.
    if (
      options.containStreamedText &&
      typeof event.content === "string" &&
      event.content.length > 0
    ) {
      let lastNonTextIndex = -1;
      for (let i = blocks.length - 1; i >= 0; i -= 1) {
        if (blocks[i].type !== "text") {
          lastNonTextIndex = i;
          break;
        }
      }
      // RESTART BACKSTOP: an earlier text block that OPENS with the same words
      // as the authoritative final answer is that answer's abandoned first
      // attempt (the model re-answered from the top after a tool round), not
      // narration — the final restates it in full. Genuine narration ("Let me
      // check the repo…") never shares the final answer's opening.
      const finalHead = restartHead(event.content);
      const containedBlocks = [
        ...blocks
          .slice(0, lastNonTextIndex + 1)
          .filter((b) => !(b.type === "text" && finalHead && restartHead(b.content) === finalHead)),
        { type: "text", content: event.content },
      ];
      return { ...message, contentBlocks: containedBlocks, isStreaming: false };
    }
    return { ...message, isStreaming: false };
  } else if (event.type === "complete") {
    if (!message?.contentBlocks?.length) return null;
    return {
      ...message,
      contentBlocks: blocks.map((block) =>
        block.type === "tool" && block.status === "running"
          ? {
              ...block,
              status: "failed",
              result: { error: "Stream ended before tool completed" },
            }
          : block,
      ),
      isStreaming: false,
      isFinal: true,
    };
  } else {
    return message;
  }

  return {
    role: "assistant",
    ...message,
    contentBlocks: blocks,
    timestamp: message?.timestamp || now,
    isStreaming: true,
  };
}

export function reduceExecutionEvent(
  state = {},
  event,
  {
    streamingTextEnabled = true,
    // stream_containment flag — default false so callers that don't pass it
    // keep byte-identical legacy behavior (kill switch lives server-side)
    containStreamedText = false,
    now = Date.now(),
  } = {},
) {
  const next = {
    progressMessages: state.progressMessages || [],
    agentStatus: state.agentStatus,
    streamingMessage: state.streamingMessage || null,
    queuedMessages: state.queuedMessages || [],
    finalData: state.finalData || null,
    isHarryActive: state.isHarryActive,
  };

  if (PROGRESS_TYPES.has(event.type)) {
    next.progressMessages = [...next.progressMessages, event.content];
    next.agentStatus = event.content;
  }

  if (streamingTextEnabled) {
    next.streamingMessage = updateStreamingMessage(
      next.streamingMessage,
      event,
      now,
      { containStreamedText },
    );
  } else if (event.type === "text") {
    next.agentStatus = "📝 Preparing response...";
  } else if (event.type === "complete") {
    next.streamingMessage = null;
  }

  if (event.type === "text_delta" && streamingTextEnabled) {
    next.agentStatus = "📝 Streaming...";
  } else if (event.type === "text_end" && streamingTextEnabled) {
    next.agentStatus = "✨ Response complete";
  } else if (event.type === "text" && streamingTextEnabled) {
    next.agentStatus = "📝 Response ready";
  } else if (event.type === "error") {
    next.finalData = {
      type: "error",
      content: event.content,
      error: { message: event.content },
      provider: event.provider || "atoms-error",
    };
  } else if (event.type === "escalation") {
    next.isHarryActive = true;
  } else if (event.type === "queued") {
    next.queuedMessages = [
      ...next.queuedMessages,
      {
        id: event.messageId,
        content: event.content,
        queuePosition: event.queuePosition,
        timestamp: now,
      },
    ];
    next.progressMessages = [
      ...next.progressMessages,
      `📨 Message queued (position ${event.queuePosition})`,
    ];
    next.finalData = { type: "queued", messageId: event.messageId };
  } else if (event.type === "queue_delivered") {
    next.queuedMessages = next.queuedMessages.filter(
      (message) => message.id !== event.messageId,
    );
    next.progressMessages = [
      ...next.progressMessages,
      "✅ Queued message delivered to agent",
    ];
  } else if (event.type === "complete") {
    next.finalData = event;
    next.progressMessages = [...next.progressMessages, "✅ Done!"];
    next.agentStatus = "✅ Done!";
    next.isHarryActive = false;
  }

  return next;
}

export default reduceExecutionEvent;
